import { createContext, useContext, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { ApiError } from "@/shared/api/errors";
import type { FeatureModule } from "@/shared/modules/types";
import type { ServiceContext, ServiceKey, ServiceMap } from "./types";

const SERVICE_KEYS: ServiceKey[] = [
  "platform",
  "session",
  "households",
  "life",
  "world",
  "visits",
  "economy",
  "farm",
  "pets",
  "reception",
  "transport",
  "companionMedia",
  "food",
  "social",
  "communicator",
  "driving",
  "homePlace",
];

/** 未接入的服务：任何方法都返回 CAPABILITY_UNAVAILABLE，绝不回退到 fixture。 */
function unavailableService<K extends ServiceKey>(key: K, _mode: string): ServiceMap[K] {
  return new Proxy({} as ServiceMap[K], {
    get(_target, prop) {
      if (prop === "fixtureScenarios" || prop === "fixtureVariants") return () => [];
      if (prop === "then") return undefined;
      return () => Promise.reject(ApiError.capability(`${key}.${String(prop)}`, "这里暂时还没开放。"));
    },
  });
}

export function buildServices(modules: FeatureModule[], ctx: ServiceContext): ServiceMap {
  const chosen: Partial<Record<ServiceKey, { owner: string; build: (c: ServiceContext) => unknown }>> = {};
  for (const module of modules) {
    for (const [key, impls] of Object.entries(module.services ?? {}) as [ServiceKey, { fixture?: unknown; live?: unknown }][]) {
      const factory = impls?.[ctx.mode] as ((c: ServiceContext) => unknown) | undefined;
      if (!factory) continue;
      const existing = chosen[key];
      if (existing) throw new Error(`服务 ${key}（${ctx.mode}）被 ${existing.owner} 与 ${module.id} 重复提供`);
      chosen[key] = { owner: module.id, build: factory };
    }
  }
  const services = {} as Record<ServiceKey, unknown>;
  for (const key of SERVICE_KEYS) {
    services[key] = chosen[key] ? chosen[key]!.build(ctx) : unavailableService(key, ctx.mode);
  }
  return services as unknown as ServiceMap;
}

const ServicesContext = createContext<ServiceMap | null>(null);

/** In-memory committed social lifetime only; never an authorization proof. */
export type SocialReaderLease = Readonly<{ isCurrent: () => boolean }>;
type SocialLeaseReader = () => SocialReaderLease;
const SocialLeaseContext = createContext<SocialLeaseReader | null>(null);

export function ServicesProvider({ services, children }: { services: ServiceMap; children: ReactNode }) {
  const committed = useRef<{ social: ServiceMap["social"]; epoch: number; active: boolean } | null>(null);
  const nextEpoch = useRef(0);
  const social = services.social;
  const reader = useMemo<SocialLeaseReader>(() => () => {
    const at = committed.current;
    const epoch = at?.epoch;
    // Capturing before a matching commit yields an unusable lease. Render never
    // revokes the old service. Every effect setup gets a fresh epoch, including
    // StrictMode's cleanup/setup; captured old leases never revive after ABA.
    return { isCurrent: () => epoch !== undefined && committed.current?.active === true && committed.current.epoch === epoch && committed.current.social === social };
  }, [social]);
  useLayoutEffect(() => {
    const lifetime = { social, epoch: ++nextEpoch.current, active: true };
    committed.current = lifetime;
    return () => { lifetime.active = false; };
  }, [social]);
  return <SocialLeaseContext.Provider value={reader}><ServicesContext.Provider value={services}>{children}</ServicesContext.Provider></SocialLeaseContext.Provider>;
}

/** Capture at operation start, after commit; a lease survives consumer unmount. */
export function useSocialLeaseReader(): SocialLeaseReader {
  const reader = useContext(SocialLeaseContext);
  if (!reader) throw new Error("useSocialLeaseReader 必须在 ServicesProvider 内使用");
  return reader;
}

export function useServices(): ServiceMap {
  const services = useContext(ServicesContext);
  if (!services) throw new Error("useServices 必须在 ServicesProvider 内使用");
  return services;
}
